<template>
  <div class="ep-page max-w-5xl">

    <!-- ── Active Session Banner ──────────────────────────── -->
    <div v-if="currentOvertimeSession"
         class="rounded-2xl p-5 relative overflow-hidden"
         style="background: rgba(251,191,36,0.07); border: 1px solid rgba(251,191,36,0.28)">
      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div class="flex items-center gap-2">
            <span class="w-2 h-2 rounded-full bg-amber-400 animate-pulse"></span>
            <h3 class="text-sm font-semibold text-amber-200">{{ t('empOvertime.currentSession') }}</h3>
          </div>
          <p class="text-sm text-amber-100/80 mt-1.5">
            {{ t('empOvertime.startedAt') }} {{ formatTime(currentOvertimeSession.startTime) }} {{ t('empOvertime.on') }} {{ formatDate(currentOvertimeSession.date) }}
          </p>
          <p class="text-xs text-amber-200/60 mt-0.5">
            {{ t('empOvertime.duration') }} {{ calculateDuration(currentOvertimeSession.startTime) }} · {{ t('empOvertime.status') }} {{ currentOvertimeSession.status }}
          </p>
        </div>
        <div class="text-left sm:text-right">
          <p class="text-2xl font-bold text-amber-300 tabular-nums">
            {{ currentOvertimeSession.type === 'weekend' ? '2.0×' : '1.5×' }}
          </p>
          <p class="text-xs text-amber-200/60">{{ t('empOvertime.rateMultiplier') }}</p>
        </div>
      </div>
    </div>

    <!-- ── Stats ──────────────────────────────────────────── -->
    <div class="grid grid-cols-2 gap-3 sm:gap-4">
      <div class="ep-card ep-card-hover p-4 sm:p-5 flex items-center gap-3.5">
        <div class="ep-stat-icon ep-stat-indigo"><Clock class="w-5 h-5" /></div>
        <div>
          <p class="text-xs font-medium text-slate-500">{{ t('empOvertime.thisMonth') }}</p>
          <p class="text-xl font-bold text-slate-100 tabular-nums">{{ stats.monthlyHours }}h</p>
        </div>
      </div>
      <div class="ep-card ep-card-hover p-4 sm:p-5 flex items-center gap-3.5">
        <div class="ep-stat-icon ep-stat-amber"><AlertCircle class="w-5 h-5" /></div>
        <div>
          <p class="text-xs font-medium text-slate-500">{{ t('empOvertime.pendingRequests') }}</p>
          <p class="text-xl font-bold text-slate-100 tabular-nums">{{ stats.pending }}</p>
        </div>
      </div>
    </div>

    <!-- ── Filters ────────────────────────────────────────── -->
    <div class="ep-card p-4 sm:p-5">
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
        <div>
          <label class="ep-label">{{ t('common.status') }}</label>
          <select v-model="filters.status" class="ep-select">
            <option value="">{{ t('empOvertime.allStatus') }}</option>
            <option value="pending">Pending</option>
            <option value="completed">Completed/Approved</option>
            <option value="rejected">Rejected</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        <div>
          <label class="ep-label">{{ t('empOvertime.fromDate') }}</label>
          <input type="date" v-model="filters.dateFrom" class="ep-input" />
        </div>
        <div>
          <label class="ep-label">{{ t('empOvertime.toDate') }}</label>
          <input type="date" v-model="filters.dateTo" class="ep-input" />
        </div>
        <button @click="loadOvertimeRequests" class="ep-btn ep-btn-primary">{{ t('empOvertime.apply') }}</button>
        <button @click="clearFilters" class="ep-btn ep-btn-secondary">{{ t('empOvertime.clear') }}</button>
      </div>
    </div>

    <!-- ── Requests List ──────────────────────────────────── -->
    <div class="ep-card overflow-hidden">
      <div class="flex items-center justify-between gap-3 px-5 py-4" style="border-bottom: 1px solid rgba(255,255,255,0.06)">
        <h3 class="ep-section-title"><Clock class="w-4 h-4 text-indigo-400" /> {{ t('empOvertime.myRequests') }}</h3>
        <button @click="showRequestForm = true" class="ep-btn ep-btn-primary ep-btn-sm shrink-0">
          <Plus class="w-4 h-4" /> {{ t('empOvertime.requestOvertime') }}
        </button>
      </div>

      <div v-if="loading" class="py-12">
        <div class="ep-spinner"></div>
        <p class="text-xs text-slate-500 text-center mt-3">{{ t('empOvertime.loading') }}</p>
      </div>

      <div v-else-if="overtimeRequests.length === 0" class="ep-empty">
        <Clock class="w-10 h-10" />
        <p class="text-sm font-medium text-slate-400">{{ t('empOvertime.noRequestsFound') }}</p>
        <p class="text-xs text-slate-600 mt-1">{{ t('empOvertime.submitFirst') }}</p>
      </div>

      <div v-else>
        <div v-for="request in overtimeRequests" :key="request.id"
             class="px-5 py-4 transition-colors hover:bg-white/[0.02]"
             style="border-bottom: 1px solid rgba(255,255,255,0.05)">
          <div class="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2 flex-wrap mb-2">
                <h4 class="text-sm font-semibold text-slate-200">
                  {{ formatDate(getSessionDate(request)) }}
                  <span class="text-slate-500 font-normal">· {{ getSessionStartTime(request) }} – {{ getSessionEndTime(request) }}</span>
                </h4>
                <span :class="getStatusBadgeClass(request.status)">
                  <span class="ep-dot"></span>{{ getStatusText(request.status) }}
                </span>
                <span v-if="request.mode" class="ep-badge ep-badge-accent">
                  {{ request.mode === 'pre_scheduled' ? t('empOvertime.scheduled') : t('empOvertime.realtime') }}
                </span>
                <span v-if="request.priority === 'high' || request.priority === 'urgent'" class="ep-badge ep-badge-danger">
                  {{ request.priority === 'urgent' ? t('empOvertime.urgent') : t('empOvertime.high') }}
                </span>
              </div>

              <div class="flex items-center gap-4 text-xs text-slate-500 mb-1.5">
                <span class="tabular-nums">{{ getSessionHours(request) }}{{ t('empOvertime.hRequested') }}</span>
                <span class="capitalize">{{ (request.session_type || '').replace('_', ' ') }}</span>
              </div>

              <p class="text-sm text-slate-400 line-clamp-2">{{ request.reason }}</p>

              <p v-if="request.approved_at && (request.status === 'completed' || request.status === 'approved')"
                 class="text-xs text-emerald-400 mt-1.5">
                ✓ {{ t('empOvertime.approvedOn') }} {{ formatDate(request.approved_at) }}
              </p>
              <p v-if="request.status === 'rejected'" class="text-xs text-red-400 mt-1.5">
                ✕ {{ t('empOvertime.rejected') }} {{ request.rejected_reason }}
              </p>
            </div>

            <div class="flex gap-2 shrink-0">
              <button v-if="request.status === 'pending'" @click="cancelRequest(request)"
                      class="ep-btn ep-btn-danger ep-btn-sm">
                {{ t('common.cancel') }}
              </button>
              <button @click="viewRequestDetails(request)" class="ep-btn ep-btn-secondary ep-btn-sm">
                {{ t('common.details') }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="totalPages > 1" class="px-5 py-3.5 flex items-center justify-between gap-3"
           style="border-top: 1px solid rgba(255,255,255,0.06)">
        <p class="text-xs text-slate-500">
          {{ t('empOvertime.showing') }} {{ (currentPage - 1) * itemsPerPage + 1 }}–{{ Math.min(currentPage * itemsPerPage, totalRequests) }} {{ t('empOvertime.of') }} {{ totalRequests }}
        </p>
        <div class="flex gap-2">
          <button @click="previousPage" :disabled="currentPage === 1" class="ep-btn ep-btn-secondary ep-btn-sm">
            {{ t('empOvertime.previous') }}
          </button>
          <button @click="nextPage" :disabled="currentPage === totalPages" class="ep-btn ep-btn-secondary ep-btn-sm">
            {{ t('empOvertime.next') }}
          </button>
        </div>
      </div>
    </div>

    <!-- ── Request Form Modal ─────────────────────────────── -->
    <div v-if="showRequestForm" class="ep-modal-backdrop" @click.self="closeRequestForm">
      <div class="ep-modal max-w-2xl">
        <div class="flex items-center justify-between px-6 py-4"
             style="border-bottom: 1px solid rgba(255,255,255,0.07)">
          <h3 class="text-base font-semibold text-slate-100">{{ t('empOvertime.requestModal') }}</h3>
          <button @click="closeRequestForm"
                  class="p-1.5 rounded-lg text-slate-500 hover:text-slate-300 hover:bg-white/5 transition-colors">
            <X class="w-5 h-5" />
          </button>
        </div>

        <form @submit.prevent="submitOvertimeRequest" class="p-6 space-y-5">
          <!-- Date and Time -->
          <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label class="ep-label">{{ t('empOvertime.date') }}</label>
              <input type="date" v-model="requestForm.requestDate" :min="todayDate" required class="ep-input" />
            </div>
            <div>
              <label class="ep-label">{{ t('empOvertime.startTime') }}</label>
              <input type="time" v-model="requestForm.startTime" required class="ep-input" />
            </div>
            <div>
              <label class="ep-label">{{ t('empOvertime.endTime') }}</label>
              <input type="time" v-model="requestForm.endTime" required class="ep-input" />
            </div>
          </div>

          <!-- Summary -->
          <div v-if="estimatedDuration > 0"
               class="rounded-xl p-4"
               style="background: rgba(99,102,241,0.07); border: 1px solid rgba(99,102,241,0.22)">
            <h4 class="text-sm font-semibold text-indigo-300 mb-2">{{ t('empOvertime.requestSummary') }}</h4>
            <div class="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span class="text-slate-500">{{ t('empOvertime.duration') }}</span>
                <span class="ml-1.5 font-medium text-slate-200 tabular-nums">{{ estimatedDuration.toFixed(1) }} hours</span>
              </div>
              <div>
                <span class="text-slate-500">{{ t('empOvertime.type') }}</span>
                <span class="ml-1.5 font-medium text-slate-200 capitalize">{{ predictedOvertimeType.replace('_', ' ') }}</span>
              </div>
            </div>
          </div>

          <!-- Priority -->
          <div>
            <label class="ep-label">{{ t('empOvertime.priorityLevel') }}</label>
            <div class="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <label v-for="opt in [
                       { value: 'low',    label: t('empOvertime.low'),           color: '#6ee7b7' },
                       { value: 'normal', label: t('empOvertime.normal'),        color: '#a5b4fc' },
                       { value: 'high',   label: t('empOvertime.highPriority'),  color: '#fdba74' },
                       { value: 'urgent', label: t('empOvertime.urgentPriority'),color: '#fca5a5' },
                     ]"
                     :key="opt.value"
                     class="flex items-center justify-center gap-2 px-3 py-2 rounded-xl cursor-pointer text-sm font-medium transition-all"
                     :style="requestForm.priority === opt.value
                       ? `background: rgba(255,255,255,0.07); border: 1px solid ${opt.color}55; color: ${opt.color}`
                       : 'background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.07); color: #64748b'">
                <input type="radio" v-model="requestForm.priority" :value="opt.value" class="sr-only" />
                {{ opt.label }}
              </label>
            </div>
          </div>

          <!-- Reason -->
          <div>
            <label class="ep-label">{{ t('empOvertime.reasonLabel') }}</label>
            <textarea v-model="requestForm.reason" required rows="3"
                      :placeholder="t('empOvertime.reasonPlaceholder')"
                      class="ep-textarea"></textarea>
          </div>

          <!-- Justification -->
          <div>
            <label class="ep-label">{{ t('empOvertime.justification') }}</label>
            <textarea v-model="requestForm.justification" rows="2"
                      :placeholder="t('empOvertime.justificationPlaceholder')"
                      class="ep-textarea"></textarea>
          </div>

          <!-- Actions -->
          <div class="flex justify-end gap-2.5 pt-4" style="border-top: 1px solid rgba(255,255,255,0.07)">
            <button type="button" @click="closeRequestForm" class="ep-btn ep-btn-ghost">{{ t('common.cancel') }}</button>
            <button type="submit" :disabled="submitting || estimatedDuration <= 0" class="ep-btn ep-btn-primary">
              {{ submitting ? t('empOvertime.submitting') : t('empOvertime.submitRequest') }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- ── Details Modal ──────────────────────────────────── -->
    <div v-if="showDetailsModal" class="ep-modal-backdrop" @click.self="showDetailsModal = false">
      <div class="ep-modal max-w-2xl">
        <div class="flex items-center justify-between px-6 py-4"
             style="border-bottom: 1px solid rgba(255,255,255,0.07)">
          <h3 class="text-base font-semibold text-slate-100">{{ t('empOvertime.detailsTitle') }}</h3>
          <button @click="showDetailsModal = false"
                  class="p-1.5 rounded-lg text-slate-500 hover:text-slate-300 hover:bg-white/5 transition-colors">
            <X class="w-5 h-5" />
          </button>
        </div>

        <div v-if="selectedRequest" class="p-6 space-y-5">
          <!-- Info grid -->
          <div class="rounded-xl p-4"
               style="background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.06)">
            <h4 class="text-sm font-semibold text-slate-200 mb-3">{{ t('empOvertime.requestInfo') }}</h4>
            <div class="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
              <div>
                <span class="text-slate-500">{{ t('empOvertime.dateLabel') }}</span>
                <span class="ml-1.5 font-medium text-slate-200">{{ formatDate(getSessionDate(selectedRequest)) }}</span>
              </div>
              <div class="flex items-center gap-1.5">
                <span class="text-slate-500">{{ t('common.status') }}:</span>
                <span :class="getStatusBadgeClass(selectedRequest.status)">{{ getStatusText(selectedRequest.status) }}</span>
              </div>
              <div>
                <span class="text-slate-500">{{ t('empOvertime.startLabel') }}</span>
                <span class="ml-1.5 font-medium text-slate-200 tabular-nums">{{ getSessionStartTime(selectedRequest) }}</span>
              </div>
              <div>
                <span class="text-slate-500">{{ t('empOvertime.endLabel') }}</span>
                <span class="ml-1.5 font-medium text-slate-200 tabular-nums">{{ getSessionEndTime(selectedRequest) }}</span>
              </div>
              <div>
                <span class="text-slate-500">{{ t('empOvertime.durationLabel') }}</span>
                <span class="ml-1.5 font-medium text-slate-200 tabular-nums">{{ getSessionHours(selectedRequest) }} hours</span>
              </div>
              <div>
                <span class="text-slate-500">{{ t('empOvertime.typeLabel') }}</span>
                <span class="ml-1.5 font-medium text-slate-200 capitalize">{{ (selectedRequest.session_type || '').replace('_', ' ') }}</span>
              </div>
              <div class="col-span-2">
                <span class="text-slate-500">{{ t('empOvertime.modeLabel') }}</span>
                <span class="ml-1.5 font-medium text-slate-200">
                  {{ selectedRequest.mode === 'pre_scheduled' ? t('empOvertime.preScheduled') : t('empOvertime.realtimeClock') }}
                </span>
              </div>
            </div>
          </div>

          <!-- Reason -->
          <div>
            <h4 class="text-sm font-semibold text-slate-200 mb-2">Reason</h4>
            <div class="rounded-xl p-3.5 text-sm text-slate-300"
                 style="background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.06)">
              {{ selectedRequest.reason }}
            </div>
          </div>

          <!-- Approval / Rejection -->
          <div v-if="selectedRequest.approved_at || selectedRequest.status === 'rejected'">
            <h4 class="text-sm font-semibold text-slate-200 mb-2">
              {{ selectedRequest.status === 'rejected' ? t('empOvertime.rejectionDetails') : t('empOvertime.approvalDetails') }}
            </h4>
            <div class="rounded-xl p-4 space-y-2"
                 style="background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.06)">
              <p v-if="selectedRequest.approved_at" class="text-sm">
                <span class="text-slate-500">{{ t('empOvertime.onLabel') }}</span>
                <span class="ml-1.5 text-slate-300">{{ formatDate(selectedRequest.approved_at) }}</span>
              </p>
              <div v-if="selectedRequest.status === 'rejected' && selectedRequest.rejected_reason">
                <p class="text-xs text-slate-500 mb-1">{{ t('empOvertime.rejectionReason') }}</p>
                <div class="rounded-lg p-3 text-sm text-red-300"
                     style="background: rgba(248,113,113,0.07); border: 1px solid rgba(248,113,113,0.22)">
                  {{ selectedRequest.rejected_reason }}
                </div>
              </div>
            </div>
          </div>

          <!-- Actual hours -->
          <div v-if="selectedRequest.actual_duration_minutes"
               class="rounded-xl p-4"
               style="background: rgba(99,102,241,0.07); border: 1px solid rgba(99,102,241,0.22)">
            <h4 class="text-sm font-semibold text-indigo-300 mb-2">{{ t('empOvertime.actualTimeWorked') }}</h4>
            <div class="text-sm space-y-1">
              <p>
                <span class="text-slate-500">{{ t('empOvertime.actualHours') }}</span>
                <span class="ml-1.5 font-medium text-slate-200 tabular-nums">
                  {{ (selectedRequest.actual_duration_minutes / 60).toFixed(1) }} hours
                </span>
              </p>
              <p v-if="selectedRequest.actual_cost">
                <span class="text-slate-500">{{ t('empOvertime.cost') }}</span>
                <span class="ml-1.5 font-medium text-slate-200 tabular-nums">
                  {{ parseFloat(selectedRequest.actual_cost).toFixed(2) }} ILS
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
const { t } = useI18n();
import {
  Clock,
  AlertCircle,
  Plus,
  X
} from 'lucide-vue-next';
import dayjs from 'dayjs';
import attendanceService from '@/services/attendance';

// Reactive data
const loading = ref(false);
const submitting = ref(false);
const showRequestForm = ref(false);
const showDetailsModal = ref(false);
const selectedRequest = ref(null);
const currentOvertimeSession = ref(null);
const overtimeRequests = ref([]);
const currentPage = ref(1);
const itemsPerPage = ref(10);
const totalRequests = ref(0);

const stats = ref({
  monthlyHours: 0,
  pending: 0,
  estimatedEarnings: 0
});

const filters = ref({
  status: '',
  dateFrom: '',
  dateTo: ''
});

const requestForm = ref({
  requestDate: '',
  startTime: '',
  endTime: '',
  reason: '',
  justification: '',
  priority: 'normal'
});

// Computed properties
const todayDate = computed(() => {
  return dayjs().format('YYYY-MM-DD');
});

const estimatedDuration = computed(() => {
  if (!requestForm.value.startTime || !requestForm.value.endTime) return 0;

  let start = dayjs(`2000-01-01 ${requestForm.value.startTime}`);
  let end = dayjs(`2000-01-01 ${requestForm.value.endTime}`);

  // Handle overnight shifts (end time is before start time)
  if (end.isBefore(start) || end.isSame(start)) {
    end = end.add(1, 'day'); // Add a day to the end time
  }

  return end.diff(start, 'hour', true);
});

const predictedOvertimeType = computed(() => {
  if (!requestForm.value.requestDate || !requestForm.value.startTime) return '';

  const requestDateTime = dayjs(`${requestForm.value.requestDate} ${requestForm.value.startTime}`);
  const dayOfWeek = requestDateTime.day(); // 0 = Sunday, 6 = Saturday
  const hour = requestDateTime.hour();

  // Sunday (0), Saturday (6), or Friday (5) = weekend overtime
  if (dayOfWeek === 0 || dayOfWeek === 5 || dayOfWeek === 6) return 'weekend';

  // Before 8 AM
  if (hour < 8) return 'before_hours';

  // After 5 PM (17:00)
  if (hour >= 17) return 'after_hours';

  // During normal hours but still requesting overtime
  return 'after_hours';
});

const predictedRateMultiplier = computed(() => {
  const type = predictedOvertimeType.value;
  switch (type) {
    case 'before_hours': return 1.25;
    case 'after_hours': return 1.50;
    case 'weekend': return 2.00;
    default: return 1.50;
  }
});

const estimatedCost = computed(() => {
  if (estimatedDuration.value <= 0) return 0;
  const hourlyRate = 25; // This should come from user profile
  return estimatedDuration.value * hourlyRate * predictedRateMultiplier.value;
});

const totalPages = computed(() => {
  return Math.ceil(totalRequests.value / itemsPerPage.value);
});

// Methods
const loadOvertimeRequests = async () => {
  try {
    loading.value = true;
    console.log('🔄 Loading overtime requests...');
    
    const response = await attendanceService.overtime.getOvertimeRequests({
      status: filters.value.status,
      dateFrom: filters.value.dateFrom,
      dateTo: filters.value.dateTo,
      page: currentPage.value,
      limit: itemsPerPage.value
    });

    const respData = response.data.data;
    // Support both legacy 'requests' and unified 'sessions' response shape
    const items = respData?.sessions || respData?.requests || [];
    if (response.data.success && items.length > 0) {
      overtimeRequests.value = items;
      totalRequests.value = respData?.pagination?.total || items.length;
      console.log('✅ Overtime sessions loaded:', overtimeRequests.value.length);
    } else {
      overtimeRequests.value = [];
      totalRequests.value = 0;
      console.log('⚠️ No overtime requests found');
    }
  } catch (error) {
    console.error('❌ Error loading overtime requests:', error);
    overtimeRequests.value = [];
    totalRequests.value = 0;
    showToast('error', 'Failed to load overtime requests');
  } finally {
    loading.value = false;
  }
};

const loadCurrentSession = async () => {
  try {
    const response = await attendanceService.overtime.getActiveOvertimeSession();
    if (response.data.success && response.data.data) {
      currentOvertimeSession.value = response.data.data;
    }
  } catch (error) {
    console.error('❌ Error loading current session:', error);
  }
};

const loadStats = async () => {
  try {
    // Calculate stats from current sessions
    const pending = overtimeRequests.value.filter(r => r.status === 'pending').length;
    const monthlyHours = overtimeRequests.value
      .filter(r => {
        const d = r.scheduled_date || r.clock_in_timestamp || r.created_at;
        return dayjs(d).isSame(dayjs(), 'month');
      })
      .reduce((total, r) => total + getSessionHours(r), 0);

    const estimatedEarnings = overtimeRequests.value
      .filter(r => r.status === 'completed' || r.status === 'approved')
      .reduce((total, r) => total + parseFloat(r.actual_cost || r.estimated_cost || 0), 0);

    stats.value = {
      pending,
      monthlyHours,
      estimatedEarnings: estimatedEarnings.toFixed(0)
    };
  } catch (error) {
    console.error('❌ Error calculating stats:', error);
  }
};

const submitOvertimeRequest = async () => {
  try {
    submitting.value = true;
    console.log('🔄 Submitting overtime request...');
    
    const response = await attendanceService.overtime.requestOvertime({
      requestDate: requestForm.value.requestDate,
      startTime: requestForm.value.startTime,
      endTime: requestForm.value.endTime,
      reason: requestForm.value.reason,
      justification: requestForm.value.justification,
      priority: requestForm.value.priority
    });

    if (response.data.success) {
      showToast('success', 'Overtime request submitted successfully!');
      closeRequestForm();
      await loadOvertimeRequests();
      await loadStats();
    } else {
      throw new Error(response.data.message || 'Failed to submit request');
    }
  } catch (error) {
    console.error('❌ Error submitting overtime request:', error);
    // Extract error message from response
    let errorMessage = 'Failed to submit overtime request';
    if (error.response?.data?.error) {
      errorMessage = error.response.data.error;
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.message) {
      errorMessage = error.message;
    }
    showToast('error', errorMessage);
  } finally {
    submitting.value = false;
  }
};

const cancelRequest = async (request) => {
  if (!confirm(`Are you sure you want to cancel this overtime request for ${formatDate(getSessionDate(request))}?`)) {
    return;
  }

  try {
    console.log('🔄 Canceling overtime request:', request.id);
    const response = await attendanceService.overtime.cancelOvertimeRequest(request.id);
    
    if (response.data.success) {
      showToast('success', 'Overtime request cancelled successfully');
      await loadOvertimeRequests();
      await loadStats();
    } else {
      throw new Error(response.data.message || 'Failed to cancel request');
    }
  } catch (error) {
    console.error('❌ Error canceling request:', error);
    showToast('error', error.message || 'Failed to cancel overtime request');
  }
};

const viewRequestDetails = (request) => {
  selectedRequest.value = request;
  showDetailsModal.value = true;
};

const closeRequestForm = () => {
  showRequestForm.value = false;
  requestForm.value = {
    requestDate: '',
    startTime: '',
    endTime: '',
    reason: '',
    justification: '',
    priority: 'normal'
  };
};

const clearFilters = () => {
  filters.value = {
    status: '',
    dateFrom: '',
    dateTo: ''
  };
  currentPage.value = 1;
  loadOvertimeRequests();
};

const previousPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
    loadOvertimeRequests();
  }
};

const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++;
    loadOvertimeRequests();
  }
};

// Utility functions
const formatDate = (date) => {
  return dayjs(date).format('MMM D, YYYY');
};

const formatTime = (time) => {
  return dayjs(`2000-01-01 ${time}`).format('h:mm A');
};

const calculateDuration = (startTime) => {
  const start = dayjs(`2000-01-01 ${startTime}`);
  const now = dayjs();
  const duration = now.diff(start, 'minute');
  const hours = Math.floor(duration / 60);
  const minutes = duration % 60;
  return `${hours}h ${minutes}m`;
};

const getStatusBadgeClass = (status) => {
  const classes = {
    pending: 'ep-badge ep-badge-warning',
    approved: 'ep-badge ep-badge-success',
    completed: 'ep-badge ep-badge-success',
    active: 'ep-badge ep-badge-info',
    under_review: 'ep-badge ep-badge-warning',
    rejected: 'ep-badge ep-badge-danger',
    cancelled: 'ep-badge ep-badge-neutral',
    expired: 'ep-badge ep-badge-neutral'
  };
  return classes[status] || 'ep-badge ep-badge-neutral';
};

const getStatusText = (status) => {
  const texts = {
    pending: 'Pending',
    approved: 'Approved (Awaiting Activation)',
    completed: 'Completed',
    active: 'Active',
    under_review: 'Under Review',
    rejected: 'Rejected',
    cancelled: 'Cancelled',
    expired: 'Expired'
  };
  return texts[status] || status;
};

// Helpers for unified session data
const getSessionDate = (session) => {
  return session.scheduled_date || session.clock_in_timestamp || session.created_at;
};

const getSessionStartTime = (session) => {
  if (session.scheduled_start_time) return session.scheduled_start_time;
  if (session.timer_start_time) return dayjs(session.timer_start_time).format('HH:mm');
  return '-';
};

const getSessionEndTime = (session) => {
  if (session.scheduled_end_time) return session.scheduled_end_time;
  if (session.timer_end_time) return dayjs(session.timer_end_time).format('HH:mm');
  return '-';
};

const getSessionHours = (session) => {
  const mins = parseFloat(session.actual_duration_minutes || session.requested_duration_minutes || 0);
  return parseFloat((mins / 60).toFixed(1));
};

const showToast = (type, message) => {
  if (type === 'success') {
    console.log('✅', message);
  } else {
    console.error('❌', message);
  }
  // Simple alert for now - could be enhanced with proper toast component
  alert(message);
};

// Watch for filter changes
watch(filters, () => {
  currentPage.value = 1;
  loadOvertimeRequests();
}, { deep: true });

// Lifecycle
onMounted(() => {
  console.log('🔄 OvertimeRequests mounted, loading data...');
  loadOvertimeRequests();
  loadCurrentSession();
  loadStats();
});
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>